import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IPerfil } from '../../models/IPerfil';
import { IFormError, IFormProps } from '../utils';
import { ICliente, IProduto, IProdutoTipoConsumo } from '../../models';
import { ITipoConsumo } from '../../models/ITipoConsumo';

interface ICreatePerfilFormFields {
  idCliente: number
  nome: string
}

interface IEditPerfilFormFields {
  idPerfil: number
  nome: string
  status: boolean
  idCliente: number
}

const createPerfilFormInitialState: IFormProps<ICreatePerfilFormFields> = {
  errors: [],
  fields: {
    idCliente: 0,
    nome: ''
  },
  touched: false,
  delivered: false
}

const editPerfilFormInitialState: IFormProps<IEditPerfilFormFields> = {
  errors: [],
  fields: {
    idPerfil: 0,
    nome: '',
    status: false,
    idCliente: 0
  },
  touched: false,
  delivered: false
}

interface IPerfilState {
  perfis: IPerfil[]
  perfisTotal: number
  createPerfilForm: IFormProps<ICreatePerfilFormFields>
  openCreatePerfilForm: boolean
  editPerfilForm: IFormProps<IEditPerfilFormFields>
  openEditPerfilForm: boolean
  perfilAVincularProduto: IPerfil | null
  clienteSelecionado?: ICliente
  editPerfilProdutosVinculados: IProdutoTipoConsumo[]
  perfilProdutosVinculados: IProdutoTipoConsumo[]
  perfisPagination: {
    page: number
    limit: number
  }
}

const initialState: IPerfilState = {
  perfis: [],
  perfisTotal: 0,
  createPerfilForm: createPerfilFormInitialState,
  openCreatePerfilForm: false,
  editPerfilForm: editPerfilFormInitialState,
  openEditPerfilForm: false,
  perfilAVincularProduto: null,
  editPerfilProdutosVinculados: [],
  perfilProdutosVinculados: [],
  perfisPagination: {
    page: 0,
    limit: 0
  }
};

export const validateCreatePerfilForm = (fields: ICreatePerfilFormFields): IFormError<keyof ICreatePerfilFormFields>[] => {
  const errors: IFormError<keyof ICreatePerfilFormFields>[] = []

  if (!fields.nome) {
    errors.push({
      key: 'nome',
      message: 'Nome é obrigatório'
    })
  } else if (!fields.idCliente || fields.idCliente === 0) {
    errors.push({
      key: 'idCliente',
      message: 'IdCliente é obrigatório'
    })
  }

  return errors
}

export const validateEditPerfilForm = (fields: IEditPerfilFormFields): IFormError<keyof IEditPerfilFormFields>[] => {
  const errors: IFormError<keyof IEditPerfilFormFields>[] = []

  if (!fields.nome) {
    errors.push({
      key: 'nome',
      message: 'Nome é obrigatório'
    })
  } else if (!fields.idPerfil || fields.idPerfil === 0) {
    errors.push({
      key: 'idPerfil',
      message: 'IdPerfil é obrigatório'
    })
  } else if (!fields.idCliente || fields.idCliente === 0) {
    errors.push({
      key: 'idCliente',
      message: 'IdCliente é obrigatório'
    })
  }

  return errors
}

const perfilSlice = createSlice({
  name: 'Perfil',
  initialState,
  reducers: {
    // Lists Actions
    setPerfisState(state, action: PayloadAction<{
      perfis: IPerfil[]
    }>) {
      state.perfis = action.payload.perfis
    },
    resetPerfisState(state) {
      state.perfis = initialState.perfis
    },
    setPerfisTotalState(state, action: PayloadAction<{
      total: number
    }>) {
      state.perfisTotal = action.payload.total
    },
    // Create Perfil Form Actions
    setOpenCreatePerfilState(state, action: PayloadAction<{
      open: boolean
    }>) {
      state.openCreatePerfilForm = action.payload.open
    },
    setCreatePerfilFormNameState(state, action: PayloadAction<{
      nome: string
    }>) {
      state.createPerfilForm.fields.nome = action.payload.nome
    },
    setCreatePerfilFormIdClienteState(state, action: PayloadAction<{
      idCliente: number
    }>) {
      state.createPerfilForm.fields.idCliente = action.payload.idCliente
    },
    resetCreatePerfilFormIdClienteState(state) {
      state.createPerfilForm.fields.idCliente = initialState.createPerfilForm.fields.idCliente
    },
    setCreatePerfilFormTouchedState(state, action: PayloadAction<{
      touched: boolean
    }>) {
      state.createPerfilForm.touched = action.payload.touched
    },
    setCreatePerfilFormDeliveredState(state, action: PayloadAction<{
      delivered: boolean
    }>) {
      state.createPerfilForm.delivered = action.payload.delivered
    },
    setCreatePerfilFormErrorsState(state, action: PayloadAction<{
      errors: IFormError<keyof ICreatePerfilFormFields>[]
    }>) {
      state.createPerfilForm.errors = action.payload.errors
    },
    resetCreatePerfilFormState(state) {
      state.createPerfilForm = {
        errors: [],
        fields: {
          nome: '',
          idCliente: 0
        },
        touched: false,
        delivered: false
      }
    },
    // Edit Perfil Form Actions
    setOpenEditPerfilState(state, action: PayloadAction<{
      open: boolean
    }>) {
      state.openEditPerfilForm = action.payload.open
    },
    setEditPerfilFormIdPerfilState(state, action: PayloadAction<{
      idPerfil: number
    }>) {
      state.editPerfilForm.fields.idPerfil = action.payload.idPerfil
    },
    setEditPerfilFormIdClienteState(state, action: PayloadAction<{
      idCliente: number
    }>) {
      state.editPerfilForm.fields.idCliente = action.payload.idCliente
    },
    setEditPerfilFormNomeState(state, action: PayloadAction<{
      nome: string
    }>) {
      state.editPerfilForm.fields.nome = action.payload.nome
    },
    setEditPerfilFormStatusState(state, action: PayloadAction<{
      status: boolean
    }>) {
      state.editPerfilForm.fields.status = action.payload.status
    },
    setEditPerfilFormTouchedState(state, action: PayloadAction<{
      touched: boolean
    }>) {
      state.editPerfilForm.touched = action.payload.touched
    },
    setEditPerfilFormDeliveredState(state, action: PayloadAction<{
      delivered: boolean
    }>) {
      state.editPerfilForm.delivered = action.payload.delivered
    },
    setEditPerfilFormErrorsState(state, action: PayloadAction<{
      errors: IFormError<keyof IEditPerfilFormFields>[]
    }>) {
      state.editPerfilForm.errors = action.payload.errors
    },
    resetEditPerfilFormState(state) {
      state.editPerfilForm = initialState.editPerfilForm
    },

    // Vincular Produto Actions
    setPerfilAVincularProdutoState(state, action: PayloadAction<{
      perfil: IPerfil | null
    }>) {
      state.perfilAVincularProduto = action.payload.perfil
    },
    setClienteSelecionadoState(state, action: PayloadAction<{
      cliente: ICliente
    }>) {
      state.clienteSelecionado = action.payload.cliente
    },
    setResetClienteSelecionadoState(state) {
      state.clienteSelecionado = initialState.clienteSelecionado
    },
    setEditPerfilProdutosVinculadosState(state, action: PayloadAction<{
      produtoTipoConsumo: IProdutoTipoConsumo[]
    }>) {
      state.editPerfilProdutosVinculados = action.payload.produtoTipoConsumo
    },
    resetEditPerfilProdutosVinculadosState(state) {
      state.editPerfilProdutosVinculados = initialState.editPerfilProdutosVinculados
    },
    setEditPerfilVincularProdutoState(state, action: PayloadAction<{
      produto: IProduto
    }>) {
      const produtosCopy = [...state.editPerfilProdutosVinculados].filter((product) => product.Produto.IdProduto !== action.payload.produto.IdProduto)

      produtosCopy.push({
        Produto: {
          ...action.payload.produto,
          Ativo: true
        },
        TipoConsumo: []
      })

      state.editPerfilProdutosVinculados = produtosCopy
    },
    setRemoveEditPerfilProdutoState(state, action: PayloadAction<{ idProduto: number }>) {
      state.editPerfilProdutosVinculados = [...state.editPerfilProdutosVinculados].filter((produto) =>
        produto.Produto.IdProduto !== action.payload.idProduto)
    },
    setEditPerfilVincularTipoConsumoState(state, action: PayloadAction<{
      Produto: IProduto
      TipoConsumo: ITipoConsumo
      Ativo: boolean
    }>) {
      const tiposConsumo: ITipoConsumo[] = []

      const newProductList: IProdutoTipoConsumo[] = []

      state.editPerfilProdutosVinculados.forEach((produtoTipoConsumo) => {
        if (produtoTipoConsumo.Produto.IdProduto !== action.payload.Produto.IdProduto) {
          newProductList.push(produtoTipoConsumo)
        } else {
          produtoTipoConsumo.TipoConsumo.forEach((tipoConsumo) => {
            if (tipoConsumo.IdTipoConsumo !== action.payload.TipoConsumo.IdTipoConsumo) {
              tiposConsumo.push(tipoConsumo)
            } else {
              if (!action.payload.Ativo) {
                tiposConsumo.push({
                  ...action.payload.TipoConsumo,
                  Ativo: false
                })
              } else {
                tiposConsumo.push({
                  ...action.payload.TipoConsumo,
                  Ativo: true
                })
              }
            }
          })

          newProductList.push({
            Produto: {
              ...action.payload.Produto,
              Ativo: action.payload.Ativo === true ? true : action.payload.Produto.Ativo
            },
            TipoConsumo: tiposConsumo
          })
        }
      })

      state.editPerfilProdutosVinculados = newProductList
    },
    setProdutosVinculadosState(state, action: PayloadAction<IProdutoTipoConsumo[]>) {
      state.perfilProdutosVinculados = action.payload
    },
    resetProdutosVinculadosState(state) {
      state.perfilProdutosVinculados = initialState.perfilProdutosVinculados
    },
    setPerfisPaginationState(state, action: PayloadAction<{ page: number; limit: number }>) {
      state.perfisPagination = {
        page: action.payload.page,
        limit: action.payload.limit
      };
    },
    setPerfisPageState(state, action: PayloadAction<{ page: number }>) {
      state.perfisPagination.page = action.payload.page
    },
    setPerfisLimitState(state, action: PayloadAction<{ limit: number }>) {
      state.perfisPagination.limit = action.payload.limit
    }
  },
});

export const {
  // Lists Actions
  setPerfisState,
  resetPerfisState,
  setPerfisTotalState,
  // Vincular Produto Actions
  setPerfilAVincularProdutoState,
  // Create Perfil Form Actions
  setOpenCreatePerfilState,
  setCreatePerfilFormIdClienteState,
  resetCreatePerfilFormIdClienteState,
  setCreatePerfilFormNameState,
  setCreatePerfilFormTouchedState,
  setCreatePerfilFormDeliveredState,
  setCreatePerfilFormErrorsState,
  resetCreatePerfilFormState,
  // Edit Perfil Form Actions
  setOpenEditPerfilState,
  setEditPerfilFormIdPerfilState,
  setEditPerfilFormIdClienteState,
  setEditPerfilFormNomeState,
  setEditPerfilFormStatusState,
  setEditPerfilFormTouchedState,
  setEditPerfilFormDeliveredState,
  setEditPerfilFormErrorsState,
  resetEditPerfilFormState,
  // Cliente Selecionado
  setClienteSelecionadoState,
  setResetClienteSelecionadoState,
  // Produtos vinculados
  resetEditPerfilProdutosVinculadosState,
  setEditPerfilProdutosVinculadosState,
  setRemoveEditPerfilProdutoState,
  setEditPerfilVincularProdutoState,
  resetProdutosVinculadosState,
  setProdutosVinculadosState,
  // setEditPerfilVincularTipoConsumoState,
  // Pagination
  setPerfisLimitState,
  setPerfisPageState,
  setPerfisPaginationState,
  setEditPerfilVincularTipoConsumoState
} = perfilSlice.actions;

export const perfilReducer = perfilSlice.reducer;